import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterNavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Collapse,
  Container,
  Navbar,
  NavbarToggler,
  Nav,
  NavItem,
  NavLink,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import Img from 'react-image';
import { connect } from 'react-redux';
import { useAuth0 } from '../react-auth0-spa';
import sandvikLogo from '../assets/sandvik_logotype_black_rgb.svg';
import {
  getJobTitles,
  getLanguages,
  getCountries,
} from '../redux/actions/applicationActions';
import { navbarRoutes } from '../routes';
import { getPermissions } from '../utils/user';

const Link = ({ to, name, selectedLink, setSelectedLink }) => (
  <NavItem className='pr-3'>
    <NavLink
      tag={RouterNavLink}
      to={to}
      onClick={() => setSelectedLink(to)}
      exact
      activeClassName={`${selectedLink === to ? 'active' : ''}`}
    >
      {name}
    </NavLink>
  </NavItem>
);

Link.propTypes = {
  to: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  selectedLink: PropTypes.string.isRequired,
  setSelectedLink: PropTypes.func.isRequired,
};

const NavBar = ({
  selectedLink,
  setSelectedLink,
  getLanguages,
  getJobTitles,
  getCountries,
  permissionsSet,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [permissions, setPermissions] = useState(getPermissions());
  const { user, isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const toggle = () => setIsOpen(!isOpen);

  useEffect(() => {
    setPermissions(getPermissions());
  }, [permissionsSet]);

  useEffect(() => {
    (async () => {
      await getLanguages();
      await getJobTitles();
      await getCountries();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logoutWithRedirect = () => {
    localStorage.removeItem('permissions');
    localStorage.removeItem('country');
    return logout({
      returnTo: window.location.origin,
    });
  };

  return (
    <div className='nav-container bg-white'>
      <Navbar light expand='md'>
        <Container fluid>
          <Img
            src={sandvikLogo}
            style={{ width: 150, height: 50 }}
            className='pr-4'
          />
          <NavbarToggler onClick={toggle} />
          <Collapse isOpen={isOpen} navbar>
            <Nav className='mr-auto' navbar>
              {navbarRoutes.map(
                ({ path, name, permissions: routePermissions = [] }) => {
                  if (!isAuthenticated) {
                    return false;
                  }

                  let isAllowed = false;

                  // If permissions array is empty than no permission is required to access it
                  if (!routePermissions.length) {
                    isAllowed = true;
                  } else {
                    routePermissions.forEach((permission) => {
                      if (permissions.includes(permission)) {
                        isAllowed = true;
                      }
                    });
                  }
                  return (
                    isAllowed && (
                      <Link
                        key={path}
                        to={path}
                        name={name}
                        selectedLink={selectedLink}
                        setSelectedLink={setSelectedLink}
                      />
                    )
                  );
                }
              )}
            </Nav>
            <Nav className='d-none d-md-block' navbar>
              {!isAuthenticated && (
                <NavItem>
                  <Button
                    id='qsLoginBtn'
                    color='primary'
                    className='btn-margin'
                    onClick={() => loginWithRedirect({})}
                  >
                    Log in
                  </Button>
                </NavItem>
              )}
              {isAuthenticated && (
                <UncontrolledDropdown nav inNavbar>
                  <DropdownToggle nav caret id='profileDropDown'>
                    <img
                      src={user.picture}
                      alt='Profile'
                      className='nav-user-profile rounded-circle'
                      width='50'
                    />
                  </DropdownToggle>
                  <DropdownMenu right>
                    <DropdownItem header>{user.name}</DropdownItem>
                    <DropdownItem
                      tag={RouterNavLink}
                      to='/profile'
                      className='dropdown-profile'
                      activeClassName='router-link-exact-active'
                    >
                      <FontAwesomeIcon icon='user' className='mr-3' /> Profile
                    </DropdownItem>
                    <DropdownItem
                      id='qsLogoutBtn'
                      onClick={() => logoutWithRedirect()}
                    >
                      <FontAwesomeIcon icon='power-off' className='mr-3' /> Log
                      out
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
            </Nav>
            {!isAuthenticated && (
              <Nav className='d-md-none' navbar>
                <NavItem>
                  <Button
                    id='qsLoginBtn'
                    color='primary'
                    block
                    onClick={() => loginWithRedirect({})}
                  >
                    Log in
                  </Button>
                </NavItem>
              </Nav>
            )}
            {isAuthenticated && (
              <Nav
                className='d-md-none justify-content-between'
                navbar
                style={{ minHeight: 170 }}
              >
                <NavItem>
                  <span className='user-info'>
                    <img
                      src={user.picture}
                      alt='Profile'
                      className='nav-user-profile d-inline-block rounded-circle mr-3'
                      width='50'
                    />
                    <h6 className='d-inline-block'>{user.name}</h6>
                  </span>
                </NavItem>
                <NavItem>
                  <FontAwesomeIcon icon='user' className='mr-3' />
                  <RouterNavLink
                    to='/profile'
                    activeClassName='router-link-exact-active'
                  >
                    Profile
                  </RouterNavLink>
                </NavItem>
                <NavItem>
                  <FontAwesomeIcon icon='power-off' className='mr-3' />
                  <RouterNavLink
                    to='#'
                    id='qsLogoutBtn'
                    onClick={() => logoutWithRedirect()}
                  >
                    Log out
                  </RouterNavLink>
                </NavItem>
              </Nav>
            )}
          </Collapse>
        </Container>
      </Navbar>
    </div>
  );
};

NavBar.propTypes = {
  selectedLink: PropTypes.string.isRequired,
  setSelectedLink: PropTypes.func.isRequired,
  getLanguages: PropTypes.func.isRequired,
  getJobTitles: PropTypes.func.isRequired,
  getCountries: PropTypes.func.isRequired,
  permissionsSet: PropTypes.bool.isRequired,
};

export default connect(null, { getLanguages, getJobTitles, getCountries })(
  NavBar
);
